import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { Observable, of, tap } from 'rxjs';
import * as fromAuth from '../../auth';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import * as fromFavorites from '../../favorites';
import * as fromLayout from '../../layout';
import { TopBarMobileResponsive } from '../components/top-bar.mobile.responsive';
import * as fromShared from '../shared';
import { AssistantBuilderPage } from './assistant-builder/assistant-builder.page';
import { AssistantCardComponent } from './assistant-card.component';
import { AssistantsTabsComponent } from './assistants-tabs.component';
import { AssistantsPageTab, filterAssistantsByTabs } from './assistants.helper';
import {
  AssistantActions,
  AssistantBuilderModalActions,
} from './store/assistants.actions';
import { assistantsFeature, AssistantsState } from './store/assistants.reducer';

type AssistantMobileDrawerAction = 'create-assistant' | 'data-boxes';

@Component({
  selector: 'squadbox-assistants',
  imports: [
    NgClass,
    TranslateModule,
    fromSeriousSystem.ButtonDirective,
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseNavigationIconDirective,
    fromSeriousSystem.DrawerComponent,
    fromSeriousSystem.OptionMenuComponent,
    fromSeriousSystem.ModalComponent,
    TopBarMobileResponsive,
    AssistantCardComponent,
    AssistantsTabsComponent,
    AssistantBuilderPage,
  ],
  template: `
    <div
      class="grid grid-rows-[min-content_auto] h-svh w-full relative [grid-template-areas:'topbar'_'content'] bg-gradient-e-vertical"
    >
      @if (isMobile()) {
        <squadbox-top-bar-mobile
          [hideMoreOptionsButton]="false"
          (chevronLeftButtonClicked)="handleChevronLeftButtonCTA()"
          (moreOptionsClicked)="isMobileDrawerOpened.set(true)"
        >
          <span class="typo-p2 font-semibold text-center w-full block">{{
            'ASSISTANTS.TITLE' | translate
          }}</span>
        </squadbox-top-bar-mobile>
      } @else {
        <header
          class="flex flex-row place-content-end gap-4 py-4 px-6 [grid-area:topbar]"
        >
          <button
            sdButton
            color="neutral"
            variant="outline"
            size="sm"
            (click)="handleClickCreateAssistantCTA()"
          >
            {{ 'ASSISTANTS.CREATE_ASSISTANT' | translate }}
          </button>
        </header>
      }
      <section
        [class.justify-center]="!assistantsForSelectedTab().length"
        [class.tablet-portrait:pt-20]="assistantsForSelectedTab().length"
        class="
        flex flex-col h-full
        w-full max-w-180 mx-auto pb-6
        align-center
        max-h-[calc(100svh - 5rem)]
        overflow-y-auto hide-scrollbar
        [grid-area:content]
      "
      >
        <!-- Assistant Library Header on Desktop -->
        @if (!isMobile() || !assistantsForSelectedTab().length) {
          <div
            class="mx-auto pointer-events-none min-h-14"
            sdIconButton
            variant="icon"
            color="neutral"
            size="lg"
          >
            <svg sdUseNavigationIcon="dashboard"></svg>
          </div>
          <div class="flex flex-col text-center">
            <h1 class="typo-h6 font-semibold">
              {{ 'ASSISTANTS.TITLE' | translate }}
            </h1>
          </div>
        }

        <!-- Tabs -->
        <squadbox-assistants-tabs
          [ngClass]="{
            'pt-6 px-4 sticky top-0 z-10': isMobile(),
            'pt-10': !isMobile(),
          }"
          [tabOptions]="tabOptions()"
          [isMobile]="isMobile()"
          [selectedTabKey]="selectedTabKey()"
        ></squadbox-assistants-tabs>

        <!-- Assistants List -->
        @if (assistantsForSelectedTab().length) {
          <ul class="flex flex-col gap-6 h-full overflow-y-auto hide-scrollbar">
            @for (
              assistant of assistantsForSelectedTab();
              track assistant.uuid
            ) {
              <squadbox-assistant-card
                [class.first:mt-6]="isMobile()"
                [class.first:mt-8]="!isMobile()"
                [class.mx-4]="isMobile()"
                [isMobile]="isMobile()"
                [logo]="assistant.logo"
                [name]="assistant.name"
                [description]="assistant.description"
                [userId]="userId() || ''"
                [ownedBy]="assistant.ownedBy"
                [status]="assistant.status"
                [isAdded]="assistant.isFavorite"
                [hasNonDeletedDocuments]="assistant.hasNonDeletedDocuments"
                (addClicked)="handleAddAssistantCTA({ uuid: assistant.uuid })"
                (removeClicked)="
                  handleRemoveAssistantCTA({ uuid: assistant.uuid })
                "
                (editClicked)="handleEditAssistantCTA({ uuid: assistant.uuid })"
                (deleteClicked)="
                  handleDeleteAssistantCTA({
                    uuid: assistant.uuid,
                    name: assistant.name,
                  })
                "
                (duplicateClicked)="
                  handleDuplicateAssistantCTA({ uuid: assistant.uuid })
                "
                (newConvoClicked)="handleNewConvoCTA({ uuid: assistant.uuid })"
              ></squadbox-assistant-card>
            }
          </ul>
        } @else {
          <div
            class="
                flex flex-col gap-1 text-center
                [&>p]:typo-p3 [&>p]:text-neutral-500
                m-16
              "
          >
            <p>
              {{ 'ASSISTANTS.SUBTITLE_NO_ASSISTANTS_LINE_1' | translate }}
            </p>
            <p>
              {{ 'ASSISTANTS.SUBTITLE_NO_ASSISTANTS_LINE_2' | translate }}
            </p>
          </div>
          <button
            sdButton
            color="primary"
            size="sm"
            class="mx-auto"
            (click)="handleClickCreateAssistantCTA()"
          >
            {{ 'ASSISTANTS.CREATE_ASSISTANT' | translate }}
          </button>
        }
      </section>

      <!-- More options -->
      @if (isMobile()) {
        <sd-drawer
          [isOpen]="isMobileDrawerOpened()"
          (closeClicked)="isMobileDrawerOpened.set(false)"
        >
          @for (option of mobileDrawerOptions(); track option.label) {
            <sd-option-menu
              [option]="option"
              (triggerAction)="handleMobileDrawerTriggerActionCTA($event)"
            ></sd-option-menu>
          }
        </sd-drawer>
      }
      <sd-modal
        [isModalVisible]="isConfirmDeleteAssistantModalOpened()"
        [modalOptions]="{
          title: 'ASSISTANTS.DELETE_ASSISTANT_MODAL.TITLE' | translate,
          showCloseButton: false,
          actions: [
            {
              label: 'ASSISTANTS.DELETE_ASSISTANT_MODAL.CTA_CANCEL' | translate,
              action: 'cancel',
              type: 'cancel',
              disabled: false,
            },
            {
              label: 'ASSISTANTS.DELETE_ASSISTANT_MODAL.CTA_DELETE' | translate,
              action: 'delete',
              type: 'delete',
              disabled: false,
            },
          ],
          backdropDismiss: true,
        }"
        (triggerAction)="
          handleConfirmDeleteAssistantModalTriggerActionCTA().action($event)
        "
      >
        <div
          class="w-auto tablet-portrait:w-120"
          innerHTML="{{
            'ASSISTANTS.DELETE_ASSISTANT_MODAL.DESCRIPTION'
              | translate
                : {
                    assistantName:
                      handleConfirmDeleteAssistantModalTriggerActionCTA()
                        .assistantName,
                  }
          }}"
        ></div>
      </sd-modal>

      <!-- Skill builder modal overlay (SB-3325 PR-4, Stage B): opened over the
           list via the store slice; create passes no assistant, edit passes the
           entity resolved from the loaded skills. -->
      @if (builderModal().kind !== 'closed') {
        <squadbox-assistant-builder [assistant]="builderAssistant()" />
      }
    </div>
  `,
})
export class AssistantsPage {
  public readonly tab = input.required<AssistantsPageTab>();

  private readonly assistantsStore = inject(Store<AssistantsState>);
  private readonly favoritesStore = inject(
    Store<fromFavorites.AssistantFavoritesState>,
  );
  private readonly favoriteEntities = this.favoritesStore.selectSignal(
    fromFavorites.assistantFavoritesFeature.selectEntities,
  );
  private readonly layoutStore = inject(Store<fromLayout.LayoutState>);
  private readonly translateService = inject(TranslateService);
  private readonly authStore = inject(Store<fromAuth.AuthState>);
  private readonly routerService = inject(fromShared.RouterService);
  private readonly mobileNavigation = inject(
    fromShared.MobileNavigationService,
  );
  private readonly confirmDialog = inject(ConfirmDialogService);

  /** Skill-builder modal state (SB-3325 PR-4, Stage B). */
  public readonly builderModal = this.assistantsStore.selectSignal(
    assistantsFeature.selectBuilderModal,
  );
  private readonly assistantEntities = this.assistantsStore.selectSignal(
    assistantsFeature.selectEntities,
  );
  /**
   * The AssistantView being edited, or undefined on create.
   *
   * Reads the one-time snapshot captured in the slice at `openEdit` — NOT a live
   * entity lookup. A live read would let a background `loadAssistants` reload
   * (`setAll`, new refs) re-fire the builder's patch/reset/share-seed effects
   * mid-edit (data loss), or drop the entity so this returns undefined and Save
   * falls onto the CREATE branch (duplicate). The snapshot keeps `[assistant]`
   * stable and defined for the modal's lifetime.
   */
  public readonly builderAssistant = computed(() => {
    const modal = this.builderModal();
    return modal.kind === 'edit' ? (modal.assistant ?? undefined) : undefined;
  });
  private readonly builderRef = viewChild(AssistantBuilderPage);

  public readonly isMobile = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectIsMobile,
  );
  private readonly assistantsFilteredByTabs = computed(() => {
    const allAssistants = this.assistantsStore.selectSignal(
      assistantsFeature.selectAll,
    );

    return filterAssistantsByTabs(allAssistants(), this.userId());
  });
  public readonly assistantsForSelectedTab = computed(() => {
    const selectedTabKey = this.tab();
    const assistants = this.assistantsFilteredByTabs()[selectedTabKey];
    const favoriteEntities = this.favoriteEntities();
    if (assistants && assistants.length > 0) {
      return assistants.map((assistant) => ({
        ...assistant,
        isFavorite: assistant.uuid in favoriteEntities,
      }));
    }
    return [];
  });
  public readonly tabOptions = computed(() => {
    const assistantsFilteredByTabs = this.assistantsFilteredByTabs();

    return [
      {
        key: 'createdByMe' as AssistantsPageTab,
        label: this.translateService.instant(
          'ASSISTANTS.TABS.CREATED_BY_ME',
        ) as string,
        disabled: assistantsFilteredByTabs.createdByMe.length === 0,
      },
      {
        key: 'sharedWithMe' as AssistantsPageTab,
        label: this.translateService.instant(
          'ASSISTANTS.TABS.SHARED_WITH_ME',
        ) as string,
        disabled: assistantsFilteredByTabs.sharedWithMe.length === 0,
      },
    ];
  });
  public readonly userId = computed(
    () => this.authStore.selectSignal(fromAuth.authFeature.selectUser)()?.id,
  );

  public readonly isMobileDrawerOpened = signal(false);
  public readonly mobileDrawerOptions = signal<fromSeriousSystem.OptionMenu[]>([
    {
      label: this.translateService.instant(
        'ASSISTANTS.CREATE_ASSISTANT',
      ) as string,
      icon: { type: 'uiBasic', icon: 'edit' },
      action: 'create-assistant' as AssistantMobileDrawerAction,
    },
  ]);
  public readonly isConfirmDeleteAssistantModalOpened = signal(false);
  public readonly handleConfirmDeleteAssistantModalTriggerActionCTA = signal<{
    action: (action: string) => void;
    assistantName: string | undefined;
  }>({ action: () => void 0, assistantName: undefined });
  public readonly selectedTabKey = computed(() => {
    const tab = this.tab();
    const tabOptions = this.tabOptions();
    const selectedTabOption = tabOptions.find((option) => option.key === tab);

    if (selectedTabOption && !selectedTabOption.disabled) {
      return selectedTabOption.key;
    }

    return 'createdByMe';
  });

  public handleAddAssistantCTA(event: { uuid: string }): void {
    this.favoritesStore.dispatch(
      fromFavorites.AssistantFavoritesActions.addFavorite({
        assistantId: event.uuid,
      }),
    );
  }

  public handleRemoveAssistantCTA(event: { uuid: string }): void {
    this.favoritesStore.dispatch(
      fromFavorites.AssistantFavoritesActions.removeFavorite({
        assistantId: event.uuid,
      }),
    );
  }

  public handleEditAssistantCTA(event: { uuid: string }) {
    // Snapshot the loaded entity at click time and hand the whole AssistantView
    // to the slice, so the open builder is immune to later store churn.
    const assistant = this.assistantEntities()[event.uuid];
    if (!assistant) {
      return;
    }
    this.assistantsStore.dispatch(
      AssistantBuilderModalActions.openEdit({ assistant }),
    );
  }

  public handleDeleteAssistantCTA(assistant: {
    uuid: string;
    name: string;
  }): void {
    this.setupConfirmDeleteAssistantModal(assistant.uuid, assistant.name);
    this.isConfirmDeleteAssistantModalOpened.set(true);
  }

  public handleDuplicateAssistantCTA(event: { uuid: string }): void {
    this.assistantsStore.dispatch(
      AssistantActions.duplicateAssistant({ uuid: event.uuid }),
    );
  }

  public async handleChevronLeftButtonCTA(): Promise<void> {
    await this.mobileNavigation.open();
  }

  public handleMobileDrawerTriggerActionCTA(action: string): void {
    // Dismiss the bottom sheet on select so it doesn't linger behind the
    // builder modal it opens.
    this.isMobileDrawerOpened.set(false);
    if ((action as AssistantMobileDrawerAction) === 'create-assistant') {
      this.handleClickCreateAssistantCTA();
    }
  }

  public handleClickCreateAssistantCTA(): void {
    this.assistantsStore.dispatch(AssistantBuilderModalActions.openCreate());
  }

  public async handleNewConvoCTA(event: { uuid: string }) {
    await this.routerService.navigateToEmptyConversationWithAssistant(
      event.uuid,
    );
  }

  private setupConfirmDeleteAssistantModal(
    assistantUuid: string,
    assistantName: string,
  ): void {
    this.handleConfirmDeleteAssistantModalTriggerActionCTA.set({
      assistantName,
      action: (action: string) => {
        switch (action as fromSeriousSystem.ModalAction['type']) {
          case 'delete':
            this.assistantsStore.dispatch(
              AssistantActions.deleteAssistant({ uuid: assistantUuid }),
            );
            this.isConfirmDeleteAssistantModalOpened.set(false);
            break;
          case 'cancel':
            this.isConfirmDeleteAssistantModalOpened.set(false);
            break;
        }
        this.handleConfirmDeleteAssistantModalTriggerActionCTA.set({
          action: () => void 0,
          assistantName: undefined,
        });
      },
    });
  }

  constructor() {
    effect(() => {
      const tab = this.tab();
      const tabOptions = this.tabOptions();
      const selectedTabOption = tabOptions.find((option) => option.key === tab);
      if (!selectedTabOption || selectedTabOption.disabled) {
        void this.routerService.navigateToAssistants('createdByMe');
      }
    });
  }

  /**
   * Browser refresh / tab close can't render a custom modal, so fall back to the
   * native prompt while the open builder holds unsaved work.
   */
  @HostListener('window:beforeunload', ['$event'])
  protected onBeforeUnload(event: BeforeUnloadEvent): void {
    if (
      this.builderModal().kind !== 'closed' &&
      this.builderRef()?.hasChanges()
    ) {
      event.preventDefault();
      // Older browsers only raise the native confirmation when `returnValue`
      // is set; modern engines honor `preventDefault()` alone. Set both so the
      // unsaved-work prompt fires everywhere (the empty string is intentional —
      // the browser substitutes its own generic message). `returnValue` is
      // spec-deprecated but remains the required cross-browser trigger here.
      // eslint-disable-next-line sonarjs/deprecation
      event.returnValue = '';
    }
  }

  /**
   * CanDeactivate hook: prompt before navigating away while the open builder
   * holds unsaved work; otherwise navigate freely. Whenever the builder is open
   * and we allow deactivation (Discard, or nothing to discard), close it so it
   * does not re-open from the surviving parent-route slice on return.
   */
  public canDeactivate(): Observable<boolean> {
    const builder = this.builderRef();
    const isModalOpen = this.builderModal().kind !== 'closed';
    if (!isModalOpen || !builder?.hasChanges()) {
      if (isModalOpen) {
        this.assistantsStore.dispatch(AssistantBuilderModalActions.close());
      }
      return of(true);
    }
    return this.confirmDialog
      .confirm({
        title: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.TITLE',
        ) as string,
        message: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.DESCRIPTION',
        ) as string,
        confirmLabel: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.CTA_DISCARD',
        ) as string,
        cancelLabel: this.translateService.instant(
          'ASSISTANTS.SAVE_MODAL_CONFIRM_CANCEL.CTA_CANCEL',
        ) as string,
        confirmType: 'delete',
      })
      .pipe(
        tap((confirmed) => {
          if (confirmed) {
            this.assistantsStore.dispatch(AssistantBuilderModalActions.close());
          }
        }),
      );
  }
}
